<template>
  <div
    @click="e => handleClick(e, 'increment')"
    @click.right="e => handleClick(e, 'decrement')"
    :class="[
      'app-card',
      app.currentCount === app.maxCount && 'app-card--maxed',
      selected && 'app-card--selected',
      disabled && 'app-card--disabled'
    ]"
    rbt-target="appItem"
  >
    <div class="app-card__column">
      <div class="app-card__thumbnail app-card__row app-card__row--gap-large">
        <img :src="`${app.icon}`" />

        <div class="app-card__meta-data">
          <span class="app-card__label">
            {{ app.text }}
          </span>

          <app-tooltip :text="t('dependencyTooltip', { dependencies: dependenciesString })">
            <span v-if="hasDependency" class="app-card__dependency">
              <v-icon>action_tree_group</v-icon>
              {{ dependencies.length === 1 ? dependencies[0] : t("dependencyCount", { count: dependencies.length }) }}
            </span>
          </app-tooltip>
        </div>
      </div>

      <div class="app-card__divider"></div>

      <div class="app-card__row app-card__row--space-between">
        <div class="app-card__row app-card__tag">
          <span class="app-card__counter">{{ t("appsAdded", { current: app.currentCount, max: app.maxCount }) }}</span>
        </div>

        <div v-if="canBeRemoved || canBeAdded" class="app-card__row">
          <v-btn
            @click.stop="decrement"
            @click.right.stop="e => e.preventDefault()"
            :class="['app-card__btn', !canBeRemoved && 'app-card__btn--disabled']"
            icon
          >
            <v-icon>action_remove_circle</v-icon>
          </v-btn>

          <span class="app-card__row app-card__row--gap-small">
            <span>{{ registryCount }}</span>
            <span>/</span>
            <span>{{ appMaxCount }}</span>
          </span>

          <v-btn
            @click.stop="increment"
            @click.right.stop="e => e.preventDefault()"
            :class="['app-card__btn', !canBeAdded && 'app-card__btn--disabled']"
            icon
          >
            <v-icon>action_add_circle</v-icon>
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "@vue/composition-api"
import { AppItem, ServiceItem } from "@/types/workspace"
import { Dictionary } from "vue-router/types/router"
import { InsertedAppRegistry } from "@/views/workspaceEdit/lib/types"
import { createTranslationModule } from "@/plugins/i18n"
import { useStore } from "@/composable/useStore"
import AppTooltip from "@/components/tooltip/TooltipRoot.vue"

export default defineComponent({
  name: "MiniAppCard",
  props: {
    app: {
      type: Object as PropType<AppItem | ServiceItem>,
      required: true
    },
    insertionScope: {
      type: Object as PropType<Dictionary<InsertedAppRegistry>>,
      required: true
    },
    renderKey: {
      type: Number,
      default: 0
    },
    disabled: {
      type: Boolean,
      default: false
    },
    dependency: {
      type: Array as PropType<string[]>,
      required: false
    }
  },
  setup(props, ctx) {
    const { spaceModule } = useStore()
    const t = createTranslationModule("WorkspaceEdit.addApps")

    const registry = computed(() => {
      const appKey = "key" in props.app ? props.app.key : props.app.type
      const _registry = props.insertionScope[appKey]
      return _registry ?? null
    })

    const hasDependency = computed(() => {
      return props.dependency && props.dependency.length > 0
    })

    const registryCount = computed(() => {
      if (registry.value) {
        return registry.value.count
      }

      return 0
    })

    const dependenciesString = computed(() => {
      if (hasDependency.value) {
        const _dependencies = props.dependency as string[]
        const apps = spaceModule.appItems.filter(app => _dependencies.includes(app.key))

        return apps.map(app => app.text).join(", ")
      }
      return ""
    })

    const dependencies = computed(() => {
      if (hasDependency.value) {
        const _dependencies = props.dependency as string[]
        const apps = spaceModule.appItems.filter(app => _dependencies.includes(app.key))

        return apps.map(app => app.text)
      }

      return []
    })

    const appMaxCount = computed(() => {
      return props.app.maxCount - props.app.currentCount
    })

    const selected = computed(() => {
      return registry.value && registry.value.count > 0
    })

    const canBeAdded = computed(() => {
      const appKey = "key" in props.app ? props.app.key : props.app.type
      const registry = props.insertionScope[appKey]
      const reachedMaxCount = props.app.maxCount === props.app.currentCount

      if (reachedMaxCount) {
        return false
      } else if (!registry) {
        return true
      }

      return registry.count < props.app.maxCount - props.app.currentCount
    })

    const canBeRemoved = computed(() => {
      const appKey = "key" in props.app ? props.app.key : props.app.type
      const registry = props.insertionScope[appKey]

      if (!registry) {
        return false
      }

      return registry.count <= registry.app.maxCount && registry.count > 0
    })

    const handleClick = (e: Event, action: "increment" | "decrement") => {
      if (props.disabled) return
      e.preventDefault()
      ctx.emit(action, props.app)
    }

    const increment = () => {
      if (props.disabled) return
      ctx.emit("increment", props.app)
    }

    const decrement = () => {
      if (props.disabled) return
      ctx.emit("decrement", props.app)
    }

    return {
      t,
      dependenciesString,
      dependencies,
      hasDependency,
      handleClick,
      appMaxCount,
      selected,
      registryCount,
      canBeRemoved,
      canBeAdded,
      increment,
      decrement
    }
  },
  components: {
    AppTooltip
  }
})
</script>
